import React, { useEffect, useState } from 'react';
import { ResolvedFileUrl, useResolvedFileUrl } from './useResolvedFileUrl';
import './resolved-image.scss';

export const PRIVATE_IMAGE_TEXT = 'Private image — sign in to view';
export const UNAVAILABLE_IMAGE_TEXT = 'Image unavailable';

export type PlaceholderKind = 'loading' | 'private' | 'unavailable';

interface ImagePlaceholderProps {
  kind: PlaceholderKind;
  className?: string;
  style?: React.CSSProperties;
  onClick?: (e: React.MouseEvent) => void;
  children?: React.ReactNode;
}

/**
 * The neutral tile shown where an image should be but can't be shown. Takes
 * the same `className` as the image so the existing size/shape rules apply
 * and the layout around it stays put.
 */
export const ImagePlaceholder: React.FC<ImagePlaceholderProps> = ({
  kind,
  className,
  style,
  onClick,
  children,
}) => {
  const text =
    kind === 'private'
      ? PRIVATE_IMAGE_TEXT
      : kind === 'unavailable'
        ? UNAVAILABLE_IMAGE_TEXT
        : '';
  return (
    <div
      className={`resolved-image-placeholder resolved-image-placeholder--${kind}${className ? ` ${className}` : ''}`}
      style={style}
      role='img'
      aria-label={text || 'Loading image'}
      data-placeholder={kind}
      onClick={onClick}
    >
      {kind !== 'loading' && (
        <>
          <span className='resolved-image-placeholder__icon' aria-hidden='true'>
            <svg
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='1.6'
            >
              <rect x='3' y='5' width='18' height='14' rx='2' />
              <circle cx='9' cy='10' r='1.6' />
              <path d='M21 16l-5-5-8 8' />
            </svg>
          </span>
          <span className='resolved-image-placeholder__text'>{text}</span>
        </>
      )}
      {children}
    </div>
  );
};

export function placeholderKindFor(
  resolved: ResolvedFileUrl,
  loadFailed: boolean,
): PlaceholderKind | null {
  if (resolved.status === 'loading') return 'loading';
  if (resolved.status === 'private') return 'private';
  if (resolved.status === 'failed' || loadFailed) return 'unavailable';
  return null;
}

interface ResolvedImageProps {
  src: string;
  alt?: string;
  className?: string;
  style?: React.CSSProperties;
  onLoad?: React.ReactEventHandler<HTMLImageElement>;
  onClick?: (e: React.MouseEvent) => void;
}

/**
 * An `<img>` that understands private-file references (`/api/files/{fileId}`)
 * and never disappears silently: private → "sign in to view" tile, missing or
 * dead → "Image unavailable" tile, same box as the image would have taken.
 */
const ResolvedImage: React.FC<ResolvedImageProps> = ({
  src,
  alt,
  className,
  style,
  onLoad,
  onClick,
}) => {
  const resolved = useResolvedFileUrl(src);
  const [loadFailed, setLoadFailed] = useState(false);
  useEffect(() => setLoadFailed(false), [src]);

  const kind = placeholderKindFor(resolved, loadFailed);
  if (kind || resolved.status !== 'ready') {
    return (
      <ImagePlaceholder
        kind={kind ?? 'unavailable'}
        className={className}
        style={style}
        onClick={onClick}
      />
    );
  }
  return (
    <img
      src={resolved.src}
      alt={alt ?? ''}
      className={className}
      style={style}
      onLoad={onLoad}
      onClick={onClick}
      onError={() => setLoadFailed(true)}
    />
  );
};

interface ResolvedBackgroundProps {
  resolved: ResolvedFileUrl;
  className?: string;
  style?: React.CSSProperties;
  onClick?: (e: React.MouseEvent) => void;
  /** Called with the URL when the (resolved) picture fails to load. */
  onLoadFailed?: (src: string) => void;
  children?: React.ReactNode;
}

/**
 * Background-image flavour of `ResolvedImage` for the card layouts that size
 * a `div` and paint the picture with `background-image`. A hidden probe
 * `<img>` tells us when the picture fails so we can swap in the placeholder.
 */
export const ResolvedBackground: React.FC<ResolvedBackgroundProps> = ({
  resolved,
  className,
  style,
  onClick,
  onLoadFailed,
  children,
}) => {
  const src = resolved.status === 'ready' ? resolved.src : null;
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const loadFailed = src !== null && failedSrc === src;

  const kind = placeholderKindFor(resolved, loadFailed);
  if (kind || src === null) {
    return (
      <ImagePlaceholder
        kind={kind ?? 'unavailable'}
        className={className}
        style={style}
        onClick={onClick}
      >
        {children}
      </ImagePlaceholder>
    );
  }
  return (
    <div
      className={className}
      style={{ ...style, backgroundImage: `url(${src})` }}
      onClick={onClick}
    >
      <img
        src={src}
        alt=''
        className='resolved-image-probe'
        onError={() => {
          setFailedSrc(src);
          onLoadFailed?.(src);
        }}
      />
      {children}
    </div>
  );
};

export default ResolvedImage;
